import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { createShopifyCart } from "@/lib/shopify";

const StoreContext = createContext(null);

const CART_KEY = "vw:cart:v2";     // bumped: line-shape changed (merchandiseId added)
const WISH_KEY = "vw:wishlist";
export const FREE_SHIPPING_THRESHOLD = 999;

const readLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readLS(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => readLS(WISH_KEY, []));
  const [isCartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);

  const addToCart = useCallback((product, size, quantity = 1) => {
    if (!product || !product.variants || product.variants.length === 0) {
      toast.error("This product has no purchasable variants yet.");
      return;
    }
    // Choose variant by size (or single-variant products).
    let variant;
    if (product.variants.length === 1) {
      variant = product.variants[0];
    } else {
      if (!size) { toast.error("Please select a size"); return; }
      variant = product.variants.find((v) => v.size === size);
      if (!variant) { toast.error("Selected size is unavailable"); return; }
    }
    if (variant.available === false) {
      toast.error("This size is currently out of stock");
      return;
    }

    setCart((prev) => {
      const key = variant.id; // Shopify variant GID is unique per {product, size}
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      const sellingPrice = variant.price ?? product.price;
      // Compare-at (MRP) — prefer the variant's, fall back to product-level.
      // Only keep it when strictly greater than the selling price.
      const mrpCandidate = variant.compareAtPrice ?? product.compareAtPrice ?? null;
      const compareAtPrice =
        typeof mrpCandidate === "number" && mrpCandidate > sellingPrice ? mrpCandidate : null;
      return [
        ...prev,
        {
          key,
          merchandiseId: variant.id,
          handle: product.handle,
          title: product.title,
          price: sellingPrice,
          compareAtPrice,
          image: product.images?.[0],
          size: variant.size || size || "",
          quantity,
        },
      ];
    });
    setCartOpen(true);
    toast.success(`${product.title} added to bag`);
  }, []);

  const updateQty = useCallback((key, delta) => {
    setCart((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeFromCart = useCallback((key) => {
    setCart((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.some((w) => w.handle === product.handle);
      if (exists) {
        toast(`${product.title} removed from wishlist`);
        return prev.filter((w) => w.handle !== product.handle);
      }
      toast.success(`${product.title} added to wishlist`);
      return [
        ...prev,
        {
          handle: product.handle,
          title: product.title,
          price: product.price,
          image: product.images?.[0],
        },
      ];
    });
  }, []);

  const isWishlisted = useCallback(
    (handle) => wishlist.some((w) => w.handle === handle),
    [wishlist]
  );

  const subtotal = useMemo(
    () => cart.reduce((s, i) => s + (i.price || 0) * i.quantity, 0),
    [cart]
  );

  // Total MRP — sum of Compare-at Price × qty for each line, falling back
  // to the selling price when no valid Compare-at exists (per spec).
  const totalMrp = useMemo(
    () => cart.reduce((s, i) => {
      const unit = (typeof i.compareAtPrice === "number" && i.compareAtPrice > (i.price || 0))
        ? i.compareAtPrice
        : (i.price || 0);
      return s + unit * i.quantity;
    }, 0),
    [cart]
  );

  // MRP discount (product-level, from Compare-at). Only positive.
  const mrpDiscount = useMemo(
    () => Math.max(0, totalMrp - subtotal),
    [totalMrp, subtotal]
  );

  // Inaugural Offer removed — selling subtotal is the final cart amount.
  const totalAmount = subtotal;

  const savings = mrpDiscount;

  const cartCount = useMemo(
    () => cart.reduce((s, i) => s + i.quantity, 0),
    [cart]
  );

  const proceedToCheckout = useCallback(async () => {
    if (cart.length === 0) return;
    setCheckoutLoading(true);
    try {
      const lines = cart.map((i) => ({ merchandiseId: i.merchandiseId, quantity: i.quantity }));
      const data = await createShopifyCart(lines);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not create checkout. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout is temporarily unavailable.");
    } finally {
      setCheckoutLoading(false);
    }
  }, [cart]);

  const value = {
    cart, wishlist, isCartOpen, setCartOpen,
    addToCart, updateQty, removeFromCart, clearCart,
    toggleWishlist, isWishlisted,
    subtotal, cartCount,
    totalMrp, mrpDiscount, totalAmount, savings,
    proceedToCheckout, checkoutLoading,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};

export const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(n || 0);
